import {
  Activity,
  Bell,
  Plus,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { CardPriceAlertDialog } from "./CardPriceAlertDialog";
import { Badge } from "../ui/badge";
import { useDeletePriceAlert } from "@/hooks/useDeletePriceAlert";
import { usePriceAlertsByCard } from "@/hooks/use-price-alerts-by-card";

type PriceAlertAction = "any" | "increase" | "decrease" | "below_market";

export const PriceAlertCard: React.FC<{ cardId: number }> = ({ cardId }) => {
  const [isOpenPriceAlertDialog, setIsOpenPriceAlertDialog] =
    React.useState(false);

  const { data } = usePriceAlertsByCard(cardId);

  const getTriggerText = (trigger: PriceAlertAction) => {
    switch (trigger) {
      case "any":
        return "Any change";
      case "increase":
        return "Price increase";
      case "decrease":
        return "Price decrease";
      case "below_market":
        return "Deal Hunter";
    }
  };

  const getTriggerIcon = (trigger: PriceAlertAction) => {
    switch (trigger) {
      case "increase":
        return <TrendingUp className="size-4 text-green-500" />;
      case "decrease":
        return <TrendingDown className="size-4 text-red-500" />;
      case "below_market":
        return <Target className="size-4 text-indigo-500" />;
      case "any":
        return <Activity className="size-4 text-blue-500" />;
      default:
        return <Bell className="size-4" />;
    }
  };

  const getTriggerColor = (trigger: PriceAlertAction) => {
    switch (trigger) {
      case "increase":
        return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700";
      case "decrease":
        return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700";
      case "below_market":
        return "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700";
      case "any":
        return "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const { mutate, isPending: isDeleting } = useDeletePriceAlert();

  return (
    <Card>
      <CardHeader>
        <div className="inline-flex w-full items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Deal Hunter & Price Alerts</h3>
            <p className="text-xs text-muted-foreground">
              Watch for below-market opportunities or a target price.
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Create a deal hunt or price alert"
            onClick={() => setIsOpenPriceAlertDialog(true)}
          >
            <Plus className="size-3" />
          </Button>
          <CardPriceAlertDialog
            cardId={cardId}
            open={isOpenPriceAlertDialog}
            onOpenChange={setIsOpenPriceAlertDialog}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex h-fit w-full flex-col gap-2">
          {data?.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center space-y-2 pb-4 text-center text-sm text-muted-foreground">
              <Target className="size-6" />
              <p>No hunts or price alerts set.</p>
              <p>Click the + button to start watching this card.</p>
            </div>
          ) : (
            data?.map((alert) => {
              const action = alert.action as PriceAlertAction;

              return (
                <div
                  key={alert.id}
                  className="flex w-full flex-row items-center gap-2"
                >
                  {getTriggerIcon(action)}
                  <Badge
                    variant="outline"
                    className={getTriggerColor(action)}
                  >
                    {getTriggerText(action)}
                  </Badge>
                  <div className="ml-auto inline-flex items-center space-x-2">
                    {action === "below_market" ? (
                      <div className="text-sm font-bold text-foreground">
                        {alert.price_value}% below 30-day average
                      </div>
                    ) : (
                      action !== "any" && (
                        <div className="text-lg font-bold text-foreground">
                          {"$"}
                          {alert.price_value}
                        </div>
                      )
                    )}
                    <Button
                      size="icon"
                      variant="destructive"
                      disabled={isDeleting}
                      aria-label="Delete alert"
                      onClick={() => mutate(alert.id)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};
